import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
// material-ui
import { useTheme } from '@mui/material/styles';

import {
	Badge,
	Box,
	Card,
	CardHeader,
	ClickAwayListener,
	FormControl,
	IconButton,
	InputAdornment,
	OutlinedInput,
	Paper,
	Popper,
	Stack,
	Typography,
	useMediaQuery,
} from '@mui/material';

// assets
import { SearchOutlined } from '@ant-design/icons';
import { useGetNotificationCountMutation } from 'store/features/process/processApi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NotificationIcon from 'assets/icons/notificationIcon';
import { CrossIcon } from 'assets/icons/common';

import { setMarkAsReadForNotification } from 'store/features/process/processSlice';
import NotificationContent from './NotificationContent';
import { encodeQueryParamData } from 'utils/utils';
import { solutionURLByVertical } from 'static-solutions/staticSolutionURLStore';

const cardHeaderSX = {
	backgroundColor: '#FFFFFF',
	color: '#0F172A',
	padding: '16px 20px',
	borderBottom: '1px solid #E2E8F0',
	display: 'flex',
	alignItems: 'center',
	'& .MuiCardHeader-content': {
		my: 0,
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiCardHeader-action': {
		m: 0,
		alignSelf: 'center',
		display: 'flex',
		alignItems: 'center',
	},
};

function TabPanel({ children, value, index, ...other }) {
	return (
		<div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
			{value === index && children}
		</div>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.any.isRequired,
	value: PropTypes.any.isRequired,
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const Notification = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
	const { unreadTotalNotificationCount } = useSelector((state) => state?.process);

	const [GetNotificationCount] = useGetNotificationCountMutation();

	// const [tab, setTab] = useState(0);

	const [searchString, setSearchString] = useState('');

	const anchorRef = useRef(null);
	const notificationModalRef = useRef(null);

	const [open, setOpen] = useState(false);

	// const handleTabChange = (event, newValue) => {
	//   setTab(newValue);
	// };

	const handleClose = () => {
		// if (!open === false) {
		//   setTab(0);
		// }

		setSearchString('');
		setOpen(false);
	};

	const clickHandler = (notification) => {
		const processCode = notification?.processCode;
		const processVersion = notification?.processVersion;
		const processName = notification?.processName;
		const vertical = notification?.vertical;
		const stageId = notification?.stageId;
		const type = notification?.type;
		const staticFormUrl = notification?.staticFormUrl;

		if (type?.toLowerCase() === 'lightwork' || type?.toLowerCase() === 'lightwork_message' || type?.toLowerCase() === 'cascade') {
			// -------------- Cascade --------------
			const content = notification?.content?.content || '';
			if (content.startsWith('Update:')) {
				navigate('/cascade?tab=outbound', { state: { cascadeId: stageId } });
			} else {
				navigate('/cascade?tab=inbound', { state: { cascadeId: stageId } });
			}
		} else if (vertical === '11' || vertical === '14') {
			// -------------- Identity & Policy --------------
			// const queryParamData = encodeQueryParamData({ processCode, processVersion });
			if (staticFormUrl?.startsWith('/static')) {
				navigate(`${staticFormUrl}/details`, { state: { processCode, _id: notification?.content?.contentId, processVersion, processName } });
			} else {
				navigate(`/case-identity/details`, { state: { processCode, _id: notification?.content?.contentId, processVersion, processName } });
			}
		} else if (vertical === '13') {
			// -------------- Task --------------
			navigate(`/process-form`, { state: { stageId, processCode, action: 'particulars' } });
			// navigate(`/process-form`, { state: { processCode: process?.codeProcess, codeTask: process?.codeTask } });
		} else if (vertical === '12') {
			// -------------- Console --------------
			const pathCode = `${processCode}-${processVersion}`;
			const path = solutionURLByVertical?.[notification?.vertical]?.[pathCode];

			if (path) {
				const queryParamData = encodeQueryParamData({ processCode, processVersion });
				navigate(`${path}/${queryParamData}`, { state: { processCode } });
			}
		}

		handleClose();
	};

	const openNotification = () => {
		if (!open) {
			dispatch(setMarkAsReadForNotification({ type: 'TOTAL' }));
			setOpen(true);
		}
	};

	useEffect(() => {
		GetNotificationCount();
	}, [GetNotificationCount]);

	useEffect(() => {
		// console.log(notificationModalRef);
		if (notificationModalRef.current) {
			// notificationModalRef.current.style.inset = "10px auto auto 20px !important";
		}
	}, [matchesXs]);

	return (
		<Box sx={{ alignSelf: 'center', flexShrink: 0 }}>
			{!matchesXs && (
				<IconButton
					disableRipple
					sx={{
						color: 'brandDark.contrastText',
						width: 'auto',
						height: 'auto',
						padding: '0 12px',
						display: 'flex',
						flexDirection: 'row',
						gap: '8px',
						alignItems: 'center',
						'&:hover': { color: 'secondary.lighter' },
					}}
					aria-label="open notification"
					ref={anchorRef}
					aria-controls={open ? 'notification-grow' : undefined}
					aria-haspopup="true"
					onClick={openNotification}
				>
					<Badge
						color="error"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						badgeContent={unreadTotalNotificationCount > -1 ? unreadTotalNotificationCount : 0}
						sx={{ '.MuiBadge-colorError': { bgcolor: '#B00020', color: '#ffffff', outline: '1px solid #ffff' } }}
						max={99}
					>
						<Stack direction="column" alignItems="center">
							{/* <img src={notification} width="16px" height="16px" alt="message" loading="lazy" /> */}
							<NotificationIcon width="20px" height="20px" />
						</Stack>
					</Badge>
					<Typography variant="body1">Notification</Typography>
				</IconButton>
			)}

			{matchesXs && (
				<IconButton
					disableRipple
					sx={{ color: 'brandDark.contrastText', '&:hover': { color: 'secondary.lighter' } }}
					aria-label="open notification"
					ref={anchorRef}
					aria-controls={open ? 'notification-grow' : undefined}
					aria-haspopup="true"
					onClick={openNotification}
				>
					<Badge
						color="error"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						badgeContent={unreadTotalNotificationCount > -1 ? unreadTotalNotificationCount : 0}
						max={99}
						sx={{ '.MuiBadge-colorError': { bgcolor: '#B00020', color: '#ffffff', outline: '1px solid #ffff' } }}
					>
						<NotificationIcon width="20px" height="20px" />
					</Badge>
				</IconButton>
			)}

			<Popper
				placement={matchesXs ? 'bottom' : 'bottom-end'}
				open={open}
				border="none"
				ref={notificationModalRef}
				anchorEl={anchorRef.current}
				role={undefined}
				disablePortal
				popperOptions={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [matchesXs ? -5 : 0, 9],
							},
						},
					],
				}}
			>
				<Paper
					sx={{
						boxShadow: '0 18px 45px rgba(15, 23, 42, 0.24)',
						minWidth: 500,
						maxWidth: 520,
						width: 520,
						borderRadius: '12px',
						border: '1px solid #E2E8F0',
						overflow: 'hidden',
						[theme.breakpoints.down('sm')]: {
							minWidth: 'unset',
							maxWidth: 'calc(100vw - 24px)',
							width: 'calc(100vw - 24px)',
							margin: '0 12px',
						},
					}}
				>
					<ClickAwayListener onClickAway={handleClose}>
						<Card
							elevation={0}
							sx={{
								position: 'relative',
								borderRadius: '12px',
							}}
						>
							<CardHeader
								titleTypographyProps={{
									sx: {
										fontSize: '20px',
										fontWeight: 600,
										color: '#101828',
										lineHeight: '28px',
									},
								}}
								action={
									<IconButton
										size="small"
										sx={{
											width: 24,
											height: 24,
											p: 0,
											color: '#99A1AF',
											'&:hover': { color: '#99A1AF', backgroundColor: 'transparent' },
										}}
										onClick={handleClose}
									>
										<CrossIcon width="24px" height="24px" color="#99A1AF" />
									</IconButton>
								}
								title="Notifications"
								sx={cardHeaderSX}
							/>

							<Box sx={{ p: '16px 20px 12px' }}>
								<FormControl sx={{ width: '100%' }}>
									<OutlinedInput
										size="small"
										id="notification-search"
										sx={{
											backgroundColor: '#F9FAFB',
											borderRadius: '12px',
											minHeight: '42px',
											'& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
										}}
										startAdornment={
											<InputAdornment position="start" sx={{ mr: -0.5 }}>
												<SearchOutlined style={{ color: '#94A3B8' }} />
											</InputAdornment>
										}
										aria-describedby="notification-search-text"
										inputProps={{
											'aria-label': 'weight',
										}}
										onChange={(e) => setSearchString(e.target.value)}
										placeholder="Search"
									/>
								</FormControl>
							</Box>
							<Box
								sx={{
									width: '100%',
									maxWidth: '100%',
									height: { xs: 'calc(100dvh - 240px)', md: 430 },
									maxHeight: { xs: 'calc(100dvh - 240px)', md: 430 },
									bgcolor: '#FFFFFF',
									position: 'relative',
									borderTop: '1px solid #E2E8F0',
								}}
							>
								<NotificationContent searchString={searchString} clickHandler={clickHandler} />
							</Box>
							<Box sx={{ textAlign: 'center', height: '10px', bgcolor: '#fff' }}></Box>
						</Card>
					</ClickAwayListener>
				</Paper>
			</Popper>
		</Box>
	);
};

export default Notification;
